import type { InspectionLocation } from '@areapif/areapif-shared'

import type { ActiveInspection } from '../models/activeInspection'
import { InspectionState } from './activeInspection'

export type TriState = 'ALL' | 'YES' | 'NO'

/**
 * Every axis the inspections table can be sliced by. All filters are applied in
 * the browser: the `inspections` table only ever holds open inspections plus the
 * current day's finished/cancelled ones (older ones are purged nightly), so the
 * whole set fits in one request and filtering client-side keeps the derived
 * columns — state, completeness — filterable exactly like the stored ones.
 *
 * The set was deliberately narrowed to the five axes operators actually reach
 * for; day range, dock, raw SOSTRAT status, level and services were dropped as
 * noise. Those columns are all still displayed and sortable in the table.
 */
export interface ActiveInspectionFilters {
  /** Case-insensitive partial match on the container id, i.e. `%fragment%`. */
  container: string
  locations: InspectionLocation[]
  states: InspectionState[]
  manual: TriState
}

/** No filtering at all — every inspection in the table. The "clear" target. */
export const EMPTY_ACTIVE_INSPECTION_FILTERS: ActiveInspectionFilters = {
  container: '',
  locations: [],
  states: [],
  manual: 'ALL',
}

/**
 * What the table opens on: the inspections still in play. Finished and
 * cancelled ones are the majority of a day's rows and are rarely what an
 * operator is looking for, so they start filtered out — clearing the filters
 * brings them back.
 */
export const DEFAULT_ACTIVE_INSPECTION_FILTERS: ActiveInspectionFilters = {
  ...EMPTY_ACTIVE_INSPECTION_FILTERS,
  states: [InspectionState.PLANNED, InspectionState.IN_PROGRESS],
}

const matchesTriState = (filter: TriState, value: boolean): boolean => filter === 'ALL' || (filter === 'YES') === value

const matchesSelection = <T>(selected: T[], value: T): boolean => selected.length === 0 || selected.includes(value)

const matchesInspection = (inspection: ActiveInspection, filters: ActiveInspectionFilters): boolean => {
  if (filters.container && !inspection.container.toUpperCase().includes(filters.container.trim().toUpperCase())) {
    return false
  }

  if (!matchesSelection(filters.locations, inspection.location)) {
    return false
  }

  if (!matchesSelection(filters.states, inspection.state)) {
    return false
  }

  return matchesTriState(filters.manual, inspection.isLocal)
}

export const applyActiveInspectionFilters = (inspections: ActiveInspection[], filters: ActiveInspectionFilters): ActiveInspection[] =>
  inspections.filter(inspection => matchesInspection(inspection, filters))

/** Number of filters currently narrowing the table, for the "clear" affordance. */
export const countAppliedFilters = (filters: ActiveInspectionFilters): number => {
  const textFilters = filters.container.trim() === '' ? 0 : 1
  const listFilters = [filters.locations, filters.states].filter(value => value.length > 0).length
  const triStateFilters = filters.manual === 'ALL' ? 0 : 1

  return textFilters + listFilters + triStateFilters
}
